import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../../../../stores/userStore';
import { captureBadgeActor, isBadgeActorCurrent } from '../../../../features/badges/progressEvents';
import { syncMemberChange } from '../../../../features/members/memberSync';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { FiX, FiSlash } from 'react-icons/fi';
import { reviewDetailApi, safetyReasons } from './reviewDetailModel';
import './ReviewSafetyDialog.css';
import { useReviewDraft } from './ReviewDraftProtection';
export default function ReviewSafetyDialog({
  target,
  en,
  assertAccount,
  onClose,
  onBlocked,
  onNotice,
}) {
  const client = useQueryClient();
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      void completedBlock.current?.flush();
    };
  }, []);
  const [reason, setReason] = useState(''),
    [details, setDetails] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false),
    [block, setBlock] = useState(false),
    [blocked, setBlocked] = useState(false);
  const pending = useRef(false),
    completedBlock = useRef(null);
  const canDiscard = useReviewDraft(!blocked && Boolean(reason || details), busy, en);
  const run = async (kind) => {
    if (pending.current) return;
    if (kind === 'report' && (!reason || (reason === 'OTHER' && !details.trim()))) return;
    const viewer = useUserStore.getState().user;
    const actor = captureBadgeActor(),
      token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    const sessionCurrent = () =>
      Boolean(
        viewer?.id &&
          token &&
          localStorage.getItem('POSTPOP_AUTH_TOKEN') === token &&
          String(useUserStore.getState().user?.id) === String(viewer.id) &&
          (!actor || isBadgeActorCurrent(actor)),
      );
    const current = () => {
      if (!alive.current || !sessionCurrent()) return false;
      try {
        assertAccount();
        return true;
      } catch {
        return false;
      }
    };
    if (!current() || String(viewer.id) === String(target.reportedUserId)) return;
    pending.current = true;
    setBusy(true);
    setError(false);
    try {
      assertAccount();
      await (kind === 'report'
        ? reviewDetailApi.report(target, reason, details)
        : reviewDetailApi.block(target));
      if (kind === 'block' && sessionCurrent()) {
        let flush;
        const operation = {
          current,
          // A browser back/unmount must also discard successful block caches.
          // Session guards remain valid after leaving the originating route.
          flush: () =>
            (flush ||= syncMemberChange(client, {
              viewer,
              current: sessionCurrent,
              target: { id: target.reportedUserId, nickname: target.displayName },
              kind: 'block',
            })),
        };
        completedBlock.current = operation;
        if (current()) setBlocked(true);
        else void operation.flush();
      } else if (current()) {
        onClose();
        onNotice(en ? 'Report submitted.' : '신고가 접수되었습니다.');
      }
    } catch {
      try {
        if (current()) setError(true);
      } catch {
        /* Old account/route must not receive this result. */
      }
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const close = async () => {
    if (pending.current) return;
    if (blocked) {
      const operation = completedBlock.current;
      if (!operation?.current()) return;
      pending.current = true;
      setBusy(true);
      // Complete the app confirmation before refreshing the containing page:
      // resetting a review/playlist query can otherwise unmount this dialog.
      const synced = await operation.flush();
      if (synced) {
        onClose();
        onBlocked();
      }
      pending.current = false;
      if (alive.current) setBusy(false);
    } else if (canDiscard()) onClose();
  };
  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      className="pp-review-dialog pp-content-safety"
      onClose={close}
      aria-labelledby="review-safety-title"
      disableEscapeKeyDown={busy}
    >
      <DialogTitle>
        <span id="review-safety-title">{en ? 'Report or Block' : '신고·차단'}</span>
        <button onClick={close} disabled={busy} aria-label={en ? 'Close' : '닫기'}>
          <FiX />
        </button>
      </DialogTitle>
      <DialogContent>
        {blocked ? (
          <>
            <p role="status">{en ? 'Member blocked.' : '회원을 차단했습니다.'}</p>
            <button disabled={busy} onClick={close}>
              {en ? 'OK' : '확인'}
            </button>
          </>
        ) : block ? (
          <>
            <p>
              {en
                ? `Block ${target.displayName}? Following will be removed and content and interactions between you will be hidden.`
                : `${target.displayName}님을 차단할까요? 서로의 팔로우가 해제되고 콘텐츠와 상호작용이 숨겨집니다.`}
            </p>
            <div className="pp-review-dialog-actions">
              <button disabled={busy} onClick={() => setBlock(false)}>
                {en ? 'Cancel' : '취소'}
              </button>
              <button disabled={busy} onClick={() => run('block')}>
                {en ? 'Block' : '차단'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p>
              {en
                ? 'Choose a reason. Reports are reviewed by the Postpop moderation team.'
                : '신고 사유를 선택해 주세요. 신고 내용은 관리자가 검토합니다.'}
            </p>
            <fieldset disabled={busy} aria-label={en ? 'Report reason' : '신고 사유'}>
              {safetyReasons.map(([value, ko, english]) => (
                <label key={value} className="pp-review-safety-reason">
                  <input
                    type="radio"
                    name="review-report"
                    value={value}
                    checked={reason === value}
                    onChange={() => setReason(value)}
                  />
                  {en ? english : ko}
                </label>
              ))}
            </fieldset>
            {reason === 'OTHER' && (
              <label>
                {en ? 'Other report details' : '기타 신고 내용'}
                <textarea
                  aria-label={en ? 'Other report details' : '기타 신고 내용'}
                  maxLength={500}
                  value={details}
                  disabled={busy}
                  onChange={(e) => setDetails(e.target.value)}
                />
                <small>{details.length} / 500</small>
              </label>
            )}
            <div className="pp-review-dialog-actions pp-safety-submit-actions">
              <button
                disabled={busy || !reason || (reason === 'OTHER' && !details.trim())}
                onClick={() => run('report')}
              >
                {en ? 'Submit report' : '신고 제출'}
              </button>
              <button disabled={busy} onClick={() => setBlock(true)}>
                <FiSlash size={19} />
                {en ? 'Block this member' : '이 회원 차단'}
              </button>
            </div>
          </>
        )}
        {error && (
          <p role="alert">
            {en
              ? 'Could not complete. Please try again.'
              : '처리하지 못했어요. 다시 시도해 주세요.'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
